import * as PrefabManager from '../PrefabManager';
import {game} from '../../Game';
import {PortalProjectileVisual} from './PortalEffects';
import * as AudioManager from '../../utils/AudioManager';

const DEFAULT_PORTAL_COLOR = '#0093ff';

class PortalBullet extends PrefabManager.basePrefab {
	constructor(target) {
		super(target);
		this.portalGun = null;
		this.impact = null;
		this.lifeTime = 3000;
		this.lifeTimer = 0;
		this.color = DEFAULT_PORTAL_COLOR;
		this.projectileVisual = null;
	}

	init() {
		super.init();
		this.body = this.lookupObject.bullet;
		this.body.SetBullet(true);
		this.body.isPortalBullet = true;
		this.createProjectileVisual();
	}

	setOwner(owner, color = DEFAULT_PORTAL_COLOR) {
		this.portalGun = owner;
		this.setColor(color);
	}

	setColor(color) {
		this.color = color || DEFAULT_PORTAL_COLOR;
		this.projectileVisual?.setColor(this.color);
	}

	createProjectileVisual() {
		if(this.projectileVisual || !this.body) return;
		this.projectileVisual = new PortalProjectileVisual(this.body, this.color);
	}

	initContactListener() {
		super.initContactListener();
		this.contactListener.PostSolve = contact => {
			if(this.impact || !this.body || this.destroyed) return;
			const bodyA = contact.GetFixtureA().GetBody();
			const bodyB = contact.GetFixtureB().GetBody();
			const bulletIsA = bodyA === this.body;
			const otherBody = bulletIsA ? bodyB : bodyA;
			const otherFixture = bulletIsA ? contact.GetFixtureB() : contact.GetFixtureA();
			const otherChildIndex = bulletIsA ?
				contact.GetChildIndexB() : contact.GetChildIndexA();
			if(otherBody.isPortalBullet || this.portalGun?.ownsBody(otherBody)) return;

			const worldManifold = new Box2D.b2WorldManifold();
			contact.GetWorldManifold(worldManifold);
			const contactPoint = worldManifold.get_points(0);
			const contactNormal = worldManifold.get_normal();
			const normalDirection = bulletIsA ? -1 : 1;
			this.impact = {
				// Linked front-face contacts are disabled by the portal's transit
				// handler, so only a solid rim/backstop impact reaches PostSolve.
				blockedByPortal: Boolean(otherBody.isPortalSurface),
				position: {x: contactPoint.get_x(), y: contactPoint.get_y()},
				angle: Math.atan2(
					contactNormal.get_y() * normalDirection,
					contactNormal.get_x() * normalDirection,
				),
				hostBody: otherBody,
				hostFixture: otherFixture,
				hostChildIndex: otherChildIndex,
			};
			Box2D.destroy(worldManifold);
		};
		this.contactListener.PreSolve = contact => {
			if(!this.body || !this.portalGun) return;
			const bodyA = contact.GetFixtureA().GetBody();
			const bodyB = contact.GetFixtureB().GetBody();
			const otherBody = bodyA === this.body ? bodyB : bodyA;
			if(this.portalGun.ownsBody(otherBody)) contact.SetEnabled(false);
		};
	}

	update() {
		if(this.destroyed) return;
		this.lifeTimer += game.editor.deltaTime;
		if(this.impact) {
			const placed = !this.impact.blockedByPortal && this.portalGun?.spawnPortal(
				this.impact.position,
				this.impact.angle,
				this.impact.hostBody,
				this.impact.hostFixture,
				this.color,
				this.impact.hostChildIndex,
			);
			if(!placed && this.portalGun && !this.portalGun.detached) {
				AudioManager.playSFX('portal_reject', 0.25, 1, this.impact.position, true);
				this.portalGun.createImpactBurst?.(this.impact.position, this.impact.angle, this.color);
			}
			this.destroy();
		} else if(this.lifeTimer >= this.lifeTime) {
			if(this.portalGun && !this.portalGun.detached) {
				AudioManager.playSFX('portal_reject', 0.25, 1, this.body.GetPosition(), true);
			}
			this.destroy();
		} else {
			this.projectileVisual?.update(game.editor.deltaTimeSeconds);
		}
	}

	destroy() {
		if(this.destroyed) return;
		this.cleanupProjectile();
		super.destroy();
	}

	cleanupProjectile() {
		this.projectileVisual?.destroy();
		this.projectileVisual = null;
		if(this.body) delete this.body.isPortalBullet;
		if(this.portalGun?.activeProjectile === this) this.portalGun.activeProjectile = null;
		this.portalGun = null;
	}

	reset() {
		this.cleanupProjectile();
	}
}

PrefabManager.prefabLibrary.PortalBullet = {
	json: '{"objects":[[0,0,0,0,"","bullet",0,["#18d5ff"],["#000"],[1],false,true,[[{"x":0,"y":0},{"x":0,"y":0}]],[1],0,[14.273075971261724],"",[1],true,false,false,[0.2],[0.05],false,false,true]]}',
	class: PortalBullet,
};

export {PortalBullet};
